/**
 * What Was Posted Table Columns
 * 
 * Column definitions for the evidence-driven Proof of Posting table.
 * Shows actual posted evidence grouped by store/campaign/operator.
 */

import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SafeImage } from '@/components/ui/safe-image';
import {
  ArrowUpDown,
  Eye,
  Image as ImageIcon,
  FileSignature,
  CheckCircle2,
  AlertCircle,
  Clock,
  Copy
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from '@/hooks/use-toast';

// Types matching backend response
export interface EvidenceItem {
  id: number;
  secureUrl: string;
  thumbnailUrl?: string; // Low-res thumbnail for fast loading
  assetType: string;
  createdAt: string;
}

export interface ExpectedFormat {
  formatId: number;
  formatName: string;
  expectedQuantity: number;
}

export interface ActualFormat {
  formatId: number;
  formatName: string;
  actualCount: number;
  expectedCount: number;
  discrepancyReason: string | null;
  completedBy: string;
}

export interface WhatWasPostedRow {
  storeId: number;
  storeName: string;
  storeAddress: string | null;
  campaignId: number;
  campaignName: string;
  operatorId: number;
  operatorName: string;
  postedAt: string;
  evidence: EvidenceItem[];
  expectedFormats: ExpectedFormat[];
  actualFormats: ActualFormat[];
  completedFormatCount: number;
  expectedFormatCount: number;
  totalPostedUnits: number;
  signatureUrl: string | null;
  signoffMethod: 'signature' | 'typed_name' | null;
  managerName: string | null;
  status: 'complete' | 'incomplete' | 'pending';
  postingEventIds: number[];
  taskId: number;
  packId: number;
  packName: string;
  notes: string | null;
  // V2: Explicit assignmentId for admin correlation (campaign_tasks.id)
  assignmentId?: number | null;
  // Phase 5: Task type for distinguishing POSTING vs REMOVAL
  taskType?: 'POSTING' | 'REMOVAL';
}

export interface WhatWasPostedColumnHandlers {
  onViewDetails: (row: WhatWasPostedRow) => void;
  onImageClick: (url: string) => void;
}

/**
 * Evidence Thumbnail Component
 */
function EvidenceThumbnails({
  evidence,
  onImageClick
}: {
  evidence: EvidenceItem[];
  onImageClick: (url: string) => void;
}) {
  const maxDisplay = 3;
  const displayedImages = evidence.slice(0, maxDisplay);
  const remainingCount = evidence.length - maxDisplay;

  if (evidence.length === 0) {
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <ImageIcon className="h-4 w-4" />
        <span className="text-xs">No images</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {displayedImages.map((img, idx) => (
        <button
          key={img.id}
          onClick={(e) => {
            e.stopPropagation();
            // Click opens full-size image
            onImageClick(img.secureUrl);
          }}
          className="relative w-10 h-10 rounded border border-gray-200 overflow-hidden hover:border-primary transition-colors"
        >
          <SafeImage
            // Use thumbnail for display (300px, fast loading), full URL on click
            src={img.thumbnailUrl || img.secureUrl}
            alt={`Evidence ${idx + 1}`}
            className="w-full h-full object-cover"
            placeholderType="photo"
            helperText=""
          />
        </button>
      ))}
      {remainingCount > 0 && (
        <Badge variant="secondary" className="text-xs">
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
}

/**
 * Status Badge Component
 */
function StatusBadge({ status }: { status: 'complete' | 'incomplete' | 'pending' }) {
  switch (status) {
    case 'complete':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    case 'incomplete':
      return (
        <Badge className="bg-orange-100 text-orange-800 border-orange-200">
          <AlertCircle className="h-3 w-3 mr-1" />
          Incomplete
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-gray-100 text-gray-600 border-gray-200">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    default:
      return null;
  }
}

/**
 * Create column definitions for What Was Posted table
 */
export function createWhatWasPostedColumns(
  handlers: WhatWasPostedColumnHandlers
): ColumnDef<WhatWasPostedRow>[] {
  return [
    // Task Type column (POSTING or REMOVAL)
    {
      id: 'taskType',
      header: 'Type',
      cell: ({ row }) => {
        const isRemoval = row.original.taskType === 'REMOVAL';
        return (
          <Badge
            variant="outline"
            className={isRemoval
              ? "text-xs bg-orange-100 text-orange-800 border-orange-300 font-medium"
              : "text-xs bg-blue-100 text-blue-800 border-blue-300 font-medium"
            }
          >
            {isRemoval ? 'Removal' : 'Posting'}
          </Badge>
        );
      },
      enableSorting: false,
    },
    // Store column
    {
      accessorKey: 'storeName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Store
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.storeName}</div>
          <div className="text-xs text-muted-foreground">
            ID: {row.original.storeId}
          </div>
        </div>
      ),
    },
    // Assignment ID column - V2: For admin correlation with mobile/backend
    {
      accessorKey: 'assignmentId',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Assign. ID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const assignmentId = row.original.assignmentId;

        if (assignmentId == null) {
          return <span className="text-muted-foreground">–</span>;
        }

        const handleCopy = (e: React.MouseEvent) => {
          e.stopPropagation();
          navigator.clipboard.writeText(String(assignmentId));
          toast({
            title: 'Copied',
            description: `Assignment ID ${assignmentId} copied to clipboard`,
          });
        };

        return (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="font-mono text-xs">
              {assignmentId}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleCopy}
              title="Copy Assignment ID"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        );
      },
    },
    // Campaign column
    {
      accessorKey: 'campaignName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Campaign
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.campaignName}</div>
          <div className="text-xs text-muted-foreground">
            {row.original.packName}
          </div>
        </div>
      ),
    },
    // Operator column
    {
      accessorKey: 'operatorName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Operator
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.original.operatorName}</div>
      ),
    },
    // Posted At column
    {
      accessorKey: 'postedAt',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Posted
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const postedAt = row.original.postedAt;
        if (!postedAt) return <span className="text-muted-foreground">-</span>;

        try {
          const date = new Date(postedAt);
          return (
            <div>
              <div className="text-sm">{format(date, 'dd MMM yyyy')}</div>
              <div className="text-xs text-muted-foreground">
                {formatDistanceToNow(date, { addSuffix: true })}
              </div>
            </div>
          );
        } catch {
          return <span className="text-muted-foreground">Invalid date</span>;
        }
      },
    },
    // Evidence column
    {
      id: 'evidence',
      header: 'Evidence',
      cell: ({ row }) => (
        <EvidenceThumbnails
          evidence={row.original.evidence || []}
          onImageClick={handlers.onImageClick}
        />
      ),
      enableSorting: false,
    },
    // Formats column
    {
      id: 'formats',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Formats
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { completedFormatCount, expectedFormatCount } = row.original;
        const isComplete = completedFormatCount >= expectedFormatCount && expectedFormatCount > 0;

        return (
          <Badge
            variant="outline"
            className={isComplete
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-orange-50 text-orange-700 border-orange-200'
            }
          >
            {completedFormatCount}/{expectedFormatCount}
          </Badge>
        );
      },
    },
    // Signature column
    {
      id: 'signature',
      header: 'Signature',
      cell: ({ row }) => {
        const signatureUrl = row.original.signatureUrl;
        const signoffMethod = row.original.signoffMethod;
        const managerName = row.original.managerName;
        if (!signatureUrl) {
          if (signoffMethod === 'typed_name' && managerName) {
            return (
              <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">
                {managerName}
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="text-gray-400 border-gray-200">
              No signature
            </Badge>
          );
        }
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlers.onImageClick(signatureUrl);
            }}
            className="flex items-center gap-1 text-green-600 hover:text-green-700"
          >
            <FileSignature className="h-4 w-4" />
            <span className="text-xs underline">View</span>
          </button>
        );
      },
      enableSorting: false,
    },
    // Status column
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handlers.onViewDetails(row.original);
          }}
        >
          <Eye className="h-4 w-4 mr-1" />
          Details
        </Button>
      ),
      enableSorting: false,
    },
  ];
}
